import type { ExtendedMenuItem, MenuItemVariant } from '@/types/order';

/**
 * Get effective price considering discount
 */
export function getEffectivePrice(price: number, discountPrice?: number): number {
  if (discountPrice && discountPrice > 0) {
    return discountPrice;
  }
  return price;
}

/**
 * Calculate variant price
 * Prefer absolute variant.price; fallback to base + adjustment
 */
export function getVariantPrice(item: ExtendedMenuItem, variant: MenuItemVariant | null): number {
  const basePrice = typeof item.price === 'string' ? parseFloat(item.price) : (item.price || 0);

  // If variant has an absolute price (not price_adjustment)
  if (variant && typeof (variant as any).price === 'number' && !Number.isNaN((variant as any).price)) {
    const variantPrice = (variant as any).price as number;
    // Use variant's discount_price if available
    return getEffectivePrice(variantPrice, variant.discount_price);
  }

  // For base item or variant with price_adjustment
  // First, get the effective base price (with discount if available)
  const effectiveBasePrice = getEffectivePrice(basePrice, item.discount_price);

  // If variant has price_adjustment, add it
  if (variant && typeof variant.price_adjustment === 'number') {
    return effectiveBasePrice + variant.price_adjustment;
  }

  return effectiveBasePrice;
}

/**
 * Get menu item name by ID
 */
export function getMenuItemName(menuItems: any[], menuItemId: number): string {
  const item = menuItems.find(m => m.id === menuItemId);
  return item?.name || 'Unknown';
}

/**
 * Get menu item category name by ID
 */
export function getMenuItemCategory(menuItems: any[], menuItemId: number): string {
  const item = menuItems.find(m => m.id === menuItemId);
  if (!item?.category) return '';
  
  // Handle both object and string category formats
  if (typeof item.category === 'string') {
    return item.category;
  }
  return item.category?.name || '';
}

/**
 * Format currency with thousands separator
 * @param amount - Amount to format
 * @param currency - Currency code (default: MXN)
 * @param locale - Locale for formatting (default: es-MX)
 * @returns Formatted currency string with thousands separator
 */
export function formatCurrency(
  amount: number, 
  currency: string = 'MXN', 
  locale: string = 'es-MX'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency
  }).format(amount);
}
